import React, { useState, useCallback, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Import } from "lucide-react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Contact from "./pages/Contact";
import Timeline from "./pages/Timeline"
import MV from "./pages/VisionMission";
import About from "./pages/About";
import SectorsCircular from "./pages/SectorGlobal";

// Service Pages
import SRPlanning from "./pages/service/planning";
import SRSurInvesti from "./pages/service/surinvesti";
import SRManagement from "./pages/service/prman";
import SRAdvisory from "./pages/service/advisory";
import SREneEnv from "./pages/service/energy";

// Article Pages
import News from "./pages/articles/News";
import Blog from "./pages/articles/Blog";
import Social from "./pages/articles/Social";
import PressRelease from "./pages/articles/PressRelease";
import CM50Post from "./pages/articles/blogs/CM50Post";
import CM50GlobalPost from "./pages/articles/blogs/CM50GlobalPost";
import CMFelicitationPost from "./pages/articles/blogs/CMFelicitationPost";
import NCUIFelicitationPost from "./pages/articles/blogs/NCUIFelicitationPost";
import RUCVisitPost from "./pages/articles/blogs/RUCVisitPost";
import RUCVisitPress from "./pages/articles/press/RUCVisitPress";
import ImageHost from "./pages/ImageHost";


const BackToTopButton = React.memo(() => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const toggleVisibility = useCallback(() => {
    const scrolled = document.documentElement.scrollTop;
    const maxHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const progress = (scrolled / maxHeight) * 100;

    setScrollProgress(progress);
    setIsVisible(scrolled > 500);
  }, []);

  const scrollToTop = useCallback(() => {
    console.log('Back to top clicked!'); // Debug log
    window.scrollTo({ 
      top: 0, 
      behavior: "smooth" 
    });
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", toggleVisibility, { passive: true });
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, [toggleVisibility]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.5, y: 20 }}
          className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-40" // Lower z-index than brochure button
        >
          <motion.button
            onClick={scrollToTop}
            onTouchEnd={scrollToTop} // Mobile touch support
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="group relative touch-manipulation"
            style={{ minHeight: '56px', minWidth: '56px' }} // Touch-friendly size
          >
            <div className="relative w-14 h-14 md:w-16 md:h-16"> {/* Smaller on mobile */}
              {/* Progress Ring */}
              <svg className="w-14 h-14 md:w-16 md:h-16 transform -rotate-90" viewBox="0 0 64 64">
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="4"
                  fill="none"
                />
                <motion.circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="rgb(185, 28, 28)"
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 28}`}
                  strokeDashoffset={`${2 * Math.PI * 28 * (1 - scrollProgress / 100)}`}
                  transition={{ duration: 0.1 }}
                />
              </svg>
              
              {/* Button Content */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-red-800 to-red-900 rounded-full flex items-center justify-center shadow-2xl group-hover:shadow-red-500/50 active:shadow-red-600/60 transition-all duration-300">
                  <ChevronDown className="w-5 h-5 md:w-6 md:h-6 text-white transform rotate-180 group-hover:translate-y-[-2px] transition-transform duration-300" />
                </div>
              </div>
            </div>
            
            {/* Tooltip - Hidden on mobile */}
            <div className="hidden md:block absolute right-full mr-4 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap shadow-xl">
                Back to Top
                <div className="absolute left-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-l-gray-900"></div>
              </div>
            </div>
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

const App = () => {
  return (
    <Router>
      <Routes>
        {/* Image hosting route without navbar/footer */}
        <Route path="/img/:imageName" element={<ImageHost />} />
        
        {/* Regular routes with navbar/footer */}
        <Route path="/*" element={
          <>
            <Navbar />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/journey" element={<Timeline />} />
              <Route path="/mission-vision" element={<MV />} />
              <Route path="/about" element={<About />} />
              <Route path="/sectors" element={<SectorsCircular />} />

              {/* initiatives */}
              <Route path="/initiatives/youth-in-politics" element={<SRPlanning />} />
              <Route path="/initiatives/youth-in-cooperatives" element={<SRSurInvesti />} />
              <Route path="/initiatives/b2c" element={<SREneEnv />} />
              <Route path="/initiatives/youth-entrepreneurship" element={<SRManagement />} />
              <Route path="/initiatives/b2r" element={<SRAdvisory />} />

              {/* articles */}
              <Route path="/articles/news" element={<News />} />
              <Route path="/articles/blog" element={<Blog />} />
              <Route path="/articles/blog/cm50-global" element={<CM50GlobalPost />} />
              <Route path="/articles/blog/cm-felicitation" element={<CMFelicitationPost />} />
              <Route path="/articles/blog/cm50" element={<CM50Post />} />
              <Route path="/articles/blog/ncui-felicitation" element={<NCUIFelicitationPost />} />
              <Route path="/articles/blog/ruc-visit" element={<RUCVisitPost />} />
              <Route path="/articles/press-release" element={<Navigate to="/media" replace />} />
              <Route path="/media" element={<PressRelease />} />
              <Route path="/media/ruc-visit" element={<RUCVisitPress />} />
              <Route path="/articles/social" element={<Social />} />
            </Routes>
            <BackToTopButton />
            <Footer />
          </>
        } />
      </Routes>
    </Router>
  );
};

export default App;